import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, UserPlus, Pencil, Users as UsersIcon, ShieldCheck } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { UsuarioFormDialog } from '@/components/usuarios/UsuarioFormDialog';

import { getUsuarios } from '@/services/api/usuarios.api';
import type { UsuarioSistema, RolUsuario } from '@/types/dominio';
import { cn, formatRelativeDate } from '@/lib/utils';

type RolFiltro = 'TODOS' | RolUsuario;

const ROL_META: Record<RolUsuario, { label: string; classes: string }> = {
  ADMIN:   { label: 'Administrador', classes: 'bg-primary-50 text-primary-700 ring-primary-200' },
  SOPORTE: { label: 'Soporte',       classes: 'bg-blue-50 text-blue-700 ring-blue-200' },
  COMPRAS: { label: 'Compras',       classes: 'bg-amber-50 text-amber-800 ring-amber-200' },
  GUARDIA: { label: 'Guardia',       classes: 'bg-slate-100 text-slate-700 ring-slate-200' },
  PEOPLE:  { label: 'Gestión Humana', classes: 'bg-violet-50 text-violet-700 ring-violet-200' },
};

function RolBadge({ rol }: { rol: RolUsuario }) {
  const meta = ROL_META[rol];
  return (
    <span className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1',
      meta.classes,
    )}>
      {meta.label}
    </span>
  );
}

function EstadoBadge({ activo }: { activo: boolean }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1',
      activo ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-100 text-slate-500 ring-slate-200',
    )}>
      <span className={cn('h-1.5 w-1.5 rounded-full', activo ? 'bg-emerald-500' : 'bg-slate-400')} />
      {activo ? 'Activo' : 'Inactivo'}
    </span>
  );
}

export function UsuariosPage() {
  const [busqueda, setBusqueda] = useState('');
  const [rol, setRol] = useState<RolFiltro>('TODOS');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState<UsuarioSistema | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['usuarios', busqueda, rol],
    queryFn: () => getUsuarios({
      busqueda: busqueda.trim() || undefined,
      rol: rol === 'TODOS' ? undefined : rol,
    }),
  });

  function abrirCrear() {
    setEditando(null);
    setDialogOpen(true);
  }

  function abrirEditar(u: UsuarioSistema) {
    setEditando(u);
    setDialogOpen(true);
  }

  return (
    <div className="space-y-6">
      {/* ── Encabezado ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-widest text-primary-600">Administración</p>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Usuarios</h2>
          <p className="text-sm text-muted-foreground">
            Gestiona las cuentas de acceso al sistema y sus roles.
          </p>
        </div>
        <Button onClick={abrirCrear}>
          <UserPlus className="h-4 w-4" />
          Nuevo usuario
        </Button>
      </div>

      {/* ── Filtros ─────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por usuario o email..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={rol} onValueChange={(v) => setRol(v as RolFiltro)}>
              <SelectTrigger><SelectValue placeholder="Rol" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos los roles</SelectItem>
                <SelectItem value="ADMIN">Administrador</SelectItem>
                <SelectItem value="SOPORTE">Soporte</SelectItem>
                <SelectItem value="COMPRAS">Compras</SelectItem>
                <SelectItem value="GUARDIA">Guardia</SelectItem>
                <SelectItem value="PEOPLE">Gestión Humana</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ── Tabla ───────────────────────────────────────────────────────── */}
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead className="hidden sm:table-cell">Estado</TableHead>
              <TableHead className="hidden lg:table-cell">Último acceso</TableHead>
              <TableHead className="text-right w-[120px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}><Skeleton className="h-10 w-full" /></TableCell>
                </TableRow>
              ))
            ) : !data?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <UsersIcon className="h-8 w-8 opacity-40" />
                    <p className="text-sm">No hay usuarios que coincidan con los filtros</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((u) => (
                <TableRow key={u.id_usuario}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 text-xs font-bold ring-1 ring-slate-200">
                        {u.username.slice(0, 2).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{u.username}</p>
                        <p className="truncate text-xs text-muted-foreground">{u.email ?? '—'}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><RolBadge rol={u.rol} /></TableCell>
                  <TableCell className="hidden sm:table-cell"><EstadoBadge activo={u.activo} /></TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {u.ultimo_login ? formatRelativeDate(u.ultimo_login) : 'Nunca'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => abrirEditar(u)}>
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="hidden xl:inline">Editar</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" />
        Las cuentas no se eliminan: para revocar el acceso, cámbialas a estado “Inactivo”.
      </p>

      <UsuarioFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        usuario={editando}
      />
    </div>
  );
}
